import Dispatcher from 'events-dispatch';
import { apiFetch } from '../../../services/api-fetch';
import debounce from '../../../services/helpers/debounce';
import { triggerEvent } from '../../../services/helpers/trigger-event';

class Feed {
    constructor(config) {
        this.config = config || {};

        this.page = this.config.page || 1;
        this.filters = this.config.filters || [];
        this.abortController = new AbortController();
        this.isRequestProcessed = false;
        this.entity = this.config.entity;
        this.el = config.el || document.body;
        this.updateSearchParams = config.setSearchParams;

        this.container =
            config.container || this.el.querySelector('[data-load-container]');
        this._method = config.method;
        this._token = config.token;

        if (config.hasOwnProperty('loadButton')) {
            this.loadButton = config.loadButton;
        } else {
            this.loadButton = this.el.querySelector('.load-more');
        }

        if (this.config.pagination) {
            this.initPagination();
        }

        this._pause = false;

        let onFilterChange = debounce(filter => {
            if (!this._pause && !this.config.explicitUpdate) {
                this.update(filter);
            }
        }, 100);
        this.filters.forEach(filter => {
            filter.on('filter.events.change', data => {
                onFilterChange(filter);
            });
        });

        if (this.loadButton) {
            this.loadButton.addEventListener('click', () => this.loadMore());
        }

        if (this.container.dataset.feedAsync) {
            this.initAsyncData(this.container.dataset.feedAsync);
        }

        if (config.reset) {
            config.reset.addEventListener('click', () => {
                this._pause = true;
                this.filters.forEach(filter => {
                    filter.reset();
                });

                this.update();
                this._pause = false;
            });
        }

        new Dispatcher(this);
    }

    applyAsyncData(element, data) {
        if (!element) {
            return;
        }

        for (const key in data) {
            element.querySelectorAll(`[data-async-target="${ key }"]`)
                .forEach((target) => {
                    const parent = target.closest(".async-stub");
                    target.textContent = data[key];

                    if (parent) {
                        parent.classList.remove("async-stub");
                    }
                });
        }

        if ("isLiked" in data) {
            element.querySelectorAll("[data-reaction=like]")
                .forEach((target) => {
                    target.classList.toggle("marked", data.isLiked);
                });
        }

        if ("isBookmarked" in data) {
            element.querySelectorAll("[data-reaction=bookmark]")
                .forEach((target) => {
                    target.classList.toggle("marked", data.isBookmarked);
                });
        }

        if (data.commentsCount === 0) {
            element.querySelectorAll("[data-async-target=commentsCount]")
                .forEach((target) => {
                    target.textContent = "Обсудить";
                });
        }
    }

    initAsyncData(path) {
        const ids = Array.from(this.container
            .querySelectorAll(`[data-entity="${ this.entity }"]`))
            .map(element => element.dataset.id);
        const url = new URL(path);

        ids.forEach(id => url.searchParams.append("ids[]", id));

        fetch(url)
            .then(res => res.json())
            .then(json => {
                for (const item of json) {
                    const { id, ...rest } = item;
                    const element = this.container.querySelector(`[data-id="${ id }"]`);
                    this.applyAsyncData(element, rest);
                }
            })
    }

    initPagination() {
        this.pagination = document.querySelector(".feed-pagination");

        if (this.pagination) {
            this.page = +this.pagination.dataset.current;
            this.pagesCount = +this.pagination.dataset.total;
            this.updatePagination();

            this.pagination.addEventListener("click", e => {
                const btn = e.target.closest(".feed-pagination__item");

                if (btn && !btn.classList.contains("active")) {
                    this.page = +btn.dataset.value;
                    this.loadPage();
                }
            });
        }
    }

    updatePagination() {
        while (this.pagination.firstChild) {
            this.pagination.removeChild(this.pagination.firstChild);
        }

        for (let i = 1; i <= this.pagesCount; ++i) {
            let visible = i == 1
                || i == this.pagesCount
                || (i >= this.page - 3 && i <= this.page)
                || (i >= this.page && i <= this.page + 3);

            if (visible) {
                this.pagination.appendChild(this.createPaginationItem(i, i == this.page));
            }
        }

        if (this.page - 3 > 1) {
            const ellipsis = this.createPaginationEllipsis();

            this.pagination.insertBefore(ellipsis, this.pagination.firstChild.nextSibling);
        }

        if (this.page + 3 < this.pagesCount) {
            const ellipsis = this.createPaginationEllipsis();

            this.pagination.insertBefore(ellipsis, this.pagination.lastChild);
        }
    }

    createPaginationEllipsis() {
        const element = document.createElement("div");

        element.textContent = "...";
        element.classList.add("feed-pagination__ellipsis");

        return element;
    }

    createPaginationItem(number, isActive) {
        const item = document.createElement("button");

        item.setAttribute("data-value", number);
        item.classList.add("feed-pagination__item", "secondary-dark-color", "btn-large", "btn", "btn-outline");
        item.classList.toggle("active", isActive);
        item.classList.toggle("btn-accent", isActive);
        item.textContent = number;

        return item;
    }

    get method() {
        return this._method;
    }

    get token() {
        return this._token;
    }

    loadPage() {
        this.setSearchParams();

        let params = this.getParams();

        this.container.setAttribute('data-loading', '');

        if (this.pagination) {
            this.pagination.setAttribute('data-loading', '');
        }

        triggerEvent(document.body, "feed.before-page-load");

        this.getCards(params)
            .then(res => {
                this.updateFeed(res.html);
                this.scrollToUp();
                this.updatePagination();
                this.updateAsync();
            })
            .catch(err => {
                console.error(err);
            })
            .finally(() => {
                this.container.removeAttribute('data-loading');
                if (this.pagination) {
                    this.pagination.removeAttribute('data-loading');
                }
            });
    }

    scrollToUp() {
        if (window.scrollTo) {
            window.scrollTo(0, 0);
        }
    }

    updateFeed(html) {
        html = html.trim();
        this.container.innerHTML = html;
        triggerEvent(document.body, 'feed.update');
    }

    updateAsync() {
        if (this.container.dataset.feedAsync) {
            this.initAsyncData(this.container.dataset.feedAsync);
        }
    }

    addCards(html) {
        this.container.insertAdjacentHTML('beforeEnd', html);
        triggerEvent(document.body, 'feed.update');
    }

    getFilters() {
        return this.filters.reduce((filtersData, filter) => {
            let filterData = filter.getData();
            return [...filtersData, ...filterData];
        }, []);
    }

    getParams() {
        let filterParams = this.getFilters();
        let params = new FormData();
        filterParams.forEach(param => {
            params.append(param[0], param[1]);
        });
        params.append('page', this.page);

        this._addRequestData(params);

        return params;
    }

    _addRequestData(params) {
        if (this.config.request) {
            for (let param in this.config.request) {
                params.append(param, this.config.request[param]);
            }
        }
        return params;
    }

    /* обновился фильтр */
    update(filter) {
        this.page = 1;

        this.setSearchParams();

        if (filter) filter.el.setAttribute('data-loading', '');
        this.container.innerHTML = '';
        this.container.setAttribute('data-loading', '');
        this.toggleLoadButton(0);

        if (this.pagination) {
            this.pagination.setAttribute('data-loading', '');
        }

        let params = this.getParams();

        this.getCards(params)
            .then(res => {
                this.updateFeed(res.html);
                this.toggleLoadButton(res.remainingPages);
                this.onUpdate(res);
            })
            .catch(err => {
                if (err.name !== "AbortError") {
                    console.error(err);
                    this.container.innerHTML =
                        '<div class="color_error">Что-то пошло не так...</div>';
                    this.toggleLoadButton(0);
                }
            })
            .finally(() => {
                if (filter) {
                    filter.el.removeAttribute('data-loading');
                }
                if (this.pagination) {
                    this.pagination.removeAttribute('data-loading');
                }
                this.container.removeAttribute('data-loading');
            });
    }

    /* подгрузить еще */
    loadMore() {
        this.page++;
        let params = this.getParams();
        if (this.loadButton) {
            this.loadButton.setAttribute('data-loading', '');
        }

        this.getCards(params)
            .then(res => {
                this.saveScroll();
                this.addCards(res.html);
                this.toggleLoadButton(res.remainingPages, res.remainingItems);
                this.onUpdate(res);
                this.restoreScroll();
                if (this.loadButton) {
                    this.loadButton.removeAttribute('data-loading');
                }
            })
            .catch(err => {
                console.error(err);
                if (this.loadButton) {
                    this.loadButton.removeAttribute('data-loading');
                }
                this.toggleLoadButton(0);
            });
    }

    saveScroll() {
        this.scrollY = window.scrollY;
    }

    restoreScroll() {
        if ("scrollY" in this && window.scrollTo) {
            window.scrollTo(0, this.scrollY);
        }
    }

    toggleLoadButton(remainingPages, remainingItems) {
        if (!this.loadButton) return;
        this.loadButton.classList.toggle('hidden', remainingPages <= 0);
        if (this.config.getMoreText) {
            this.loadButton.innerHTML = this.config.getMoreText(remainingItems);
        }
    }

    getCards(params) {
        if (this.token) {
            params.append("token", this.token);
        }

        if (this.isRequestProcessed) {
            this.abortController.abort();
            this.abortController = new AbortController();
        }

        this.isRequestProcessed = true;

        let url = this.method;
        let body = params;

        if (this.config.requestMethod === "GET") {
            url = new URL(url, window.location.origin);
            body = null;

            for (const [ key, value ] of params.entries()) {
                url.searchParams.append(key, value);
            }
        }

        return apiFetch(url, {
            method: this.config.requestMethod || 'POST',
            body: body,
            signal: this.abortController.signal
        })
            .then(res => {
                if (!res) throw new Error('Empty response');

                return res;
            })
            .catch(error => {
                if (error.name !== "AbortError" && this.config.onError) {
                    this.config.onError(error);
                }
                else {
                    throw error;
                }
            })
            .finally(() => {
                this.isRequestProcessed = false;
            });
    }

    onUpdate(res) {
        document.body.dispatchEvent(new Event(Feed.events.update));
        this.trigger(Feed.events.update, res);

        if (window.highlight) {
            window.highlight.initBlock(this.container);
        }

        if (this.pagination) {
            this.pagesCount = this.page + res.remainingPages;
            this.updatePagination();
        }

        this.updateAsync();

        this.filters.forEach(filter => {
            filter.update(res.filter);
        });
    }

    setSearchParams() {
        if (!this.updateSearchParams) return;
        let params = this.filters
            .filter(filter => filter.needUpdateUrl)
            .map(filter => filter.searchParams);

        if (this.pagination) {
            params.push("page=" + this.page);
        }

        params = params.join('&');

        history.pushState(
            null,
            null,
            location.origin + location.pathname + '?' + params
        );
    }

    reset() {
        this.filters.forEach(filter => {
            filter.reset();
        });
    }
}

Feed.events = {
    update: 'feed.events.update'
};

export default Feed;
